import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Loader2, CheckCircle2, Clock, AlertTriangle,
  ChevronRight, Star, FileDown,
} from 'lucide-react'
import { supabase } from '@/config/supabase'
import { ROUTES } from '@/config/routes'
import useBookingStore from '@/stores/bookingStore'
import Button from '@/components/ui/Button'

const fmt = (n) =>
  n != null && !isNaN(n)
    ? '\u20b1' + Number(n).toLocaleString('en-PH', { minimumFractionDigits: 0 })
    : '\u2014'

const LOADING_MESSAGES = [
  'Analyzing your request\u2026',
  'Reviewing service rates\u2026',
  'Preparing your Scope Estimate\u2026',
  'Almost ready\u2026',
]

const ACCURACY_OPTIONS = [
  { val: 'yes_perfectly', label: 'Yes, perfectly' },
  { val: 'partially',     label: 'Partially' },
  { val: 'not_really',    label: 'Not really' },
]

export default function ViewQuote() {
  const { category: categorySlug } = useParams()
  const navigate = useNavigate()

  const bookingId = useBookingStore((s) => s.bookingId)

  const [status,  setStatus]  = useState('generating')
  const [data,    setData]    = useState(null)
  const [msgIdx,  setMsgIdx]  = useState(0)

  // Feedback form state
  const [fbRating,   setFbRating]   = useState(0)
  const [fbHover,    setFbHover]    = useState(0)
  const [fbAccuracy, setFbAccuracy] = useState('')
  const [fbNotes,    setFbNotes]    = useState('')
  const [fbSent,     setFbSent]     = useState(false)
  const [fbSending,  setFbSending]  = useState(false)

  // Cycle loading messages
  useEffect(() => {
    if (status !== 'generating') return
    const t = setInterval(() => setMsgIdx((i) => (i + 1) % LOADING_MESSAGES.length), 3000)
    return () => clearInterval(t)
  }, [status])

  useEffect(() => {
    if (!bookingId) { navigate('/book', { replace: true }); return }
    generateQuote()
  }, []) // eslint-disable-line

  async function generateQuote() {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await supabase.functions.invoke('generate-quote', {
        body: { booking_id: bookingId },
        headers: { Authorization: 'Bearer ' + session?.access_token },
      })

      if (res.error) {
        console.error('Edge function error:', res.error)
        setStatus('error')
        return
      }

      setData(res.data)
      setStatus('ready')
    } catch (err) {
      console.error('generateQuote error:', err)
      setStatus('error')
    }
  }

  // ── CTA actions ─────────────────────────────────────────────────────────────

  function handleFinalProposal() {
    navigate(ROUTES.BOOK_FINAL_PROPOSAL)
  }

  function handleThinkFirst() {
    // Quote is already saved in DB — just reset in-memory state and go home
    useBookingStore.getState().reset()
    navigate(ROUTES.BOOK, { replace: true })
  }

  function handleStartNew() {
    useBookingStore.getState().reset()
    navigate(ROUTES.BOOK, { replace: true })
  }

  // ── Feedback submission ──────────────────────────────────────────────────────

  async function submitFeedback() {
    if (fbSending) return
    setFbSending(true)
    try {
      await supabase.from('ai_quote_feedback').insert({
        booking_id:     bookingId,
        quote_id:       data?.quote_id ?? null,
        rating:         fbRating || null,
        accuracy:       fbAccuracy || null,
        missed_notes:   fbNotes || null,
        feedback_stage: 1,
      })
    } catch (err) {
      console.error('feedback error:', err)
    }
    setFbSent(true)
    setFbSending(false)
  }

  // ── Generating ──────────────────────────────────────────────────────────────
  if (status === 'generating') {
    return (
      <div className="min-h-dvh flex flex-col items-center justify-center bg-surface-base gap-6 px-4">
        <div className="w-16 h-16 bg-siglat-teal/15 rounded-2xl flex items-center justify-center animate-pulse">
          <span className="text-siglat-teal text-xl font-black">SK</span>
        </div>
        <div className="flex flex-col items-center gap-2 text-center">
          <Loader2 className="w-6 h-6 text-siglat-teal animate-spin" />
          <p className="text-ink-primary font-semibold">{LOADING_MESSAGES[msgIdx]}</p>
          <p className="text-sm text-ink-muted">Prepared by Siglat Konstract</p>
        </div>
      </div>
    )
  }

  // ── Error ───────────────────────────────────────────────────────────────────
  if (status === 'error') {
    return (
      <div className="min-h-dvh flex flex-col items-center justify-center bg-surface-base gap-4 px-4 text-center">
        <AlertTriangle className="w-10 h-10 text-siglat-red" />
        <div>
          <p className="font-semibold text-ink-primary">Could not generate estimate</p>
          <p className="text-sm text-ink-muted mt-1">Please try again or contact Siglat Konstract.</p>
        </div>
        <div className="flex gap-3">
          <Button variant="secondary" onClick={() => { setStatus('generating'); generateQuote() }}>
            Try Again
          </Button>
          <Button variant="ghost" onClick={() => navigate('/book')}>Go Back</Button>
        </div>
      </div>
    )
  }

  // ── Ready ───────────────────────────────────────────────────────────────────
  const boq        = data?.boq
  const sections   = boq?.sections ?? []
  const summary    = boq?.summary ?? {}
  const grandTotal = summary.grand_total_client ?? 0
  const totalMin   = data?.total_min ?? Math.round(grandTotal * 0.9)
  const totalMax   = data?.total_max ?? Math.round(grandTotal * 1.1)
  const quoteRef   = data?.quote_reference ?? ''
  const confidence = data?.ai_confidence ?? 'medium'
  const flags      = data?.flags ?? []

  return (
    <div className="min-h-dvh bg-surface-base flex flex-col">

      {/* Header */}
      <div className="bg-surface-raised border-b border-surface-border px-4 pt-12 pb-4 safe-top shrink-0">
        <div className="page-container">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle2 className="w-5 h-5 text-siglat-teal" />
            <h1 className="font-display text-xl font-bold text-ink-primary">Scope Estimate</h1>
          </div>
          <p className="text-xs text-ink-muted">Prepared by Siglat Konstract</p>
          {quoteRef && (
            <p className="text-xs text-siglat-teal font-mono mt-1">{quoteRef} &bull; Valid for 30 days</p>
          )}
          <span className={`inline-block mt-2 text-xs px-2 py-0.5 rounded-full font-medium
            ${confidence === 'high'   ? 'bg-green-500/15 text-green-400' :
              confidence === 'medium' ? 'bg-yellow-500/15 text-yellow-400' :
                                        'bg-siglat-red/15 text-siglat-red'}`}>
            Estimate Reliability: {confidence === 'high' ? 'High' : confidence === 'medium' ? 'Medium' : 'Low'}
          </span>
        </div>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto">
        <div className="page-container py-5 flex flex-col gap-4">

          {/* Scope summary */}
          <div className="bg-surface-raised border border-surface-border rounded-xl p-4">
            <p className="text-xs font-medium text-ink-muted uppercase tracking-widest mb-2">Scope of Work</p>
            <p className="text-ink-primary leading-relaxed text-sm">{data?.scope_summary}</p>
            {data?.est_duration && (
              <div className="flex items-center gap-1.5 mt-3 text-ink-muted">
                <Clock className="w-3.5 h-3.5" />
                <span className="text-xs">Estimated duration: {data.est_duration}</span>
              </div>
            )}
          </div>

          {/* BOQ Sections */}
          {sections.map((section) => (
            <div key={section.section_number} className="bg-surface-raised border border-surface-border rounded-xl overflow-hidden">
              <div className="px-4 py-2.5 border-b border-surface-border bg-surface-overlay">
                <p className="text-xs font-semibold text-siglat-teal uppercase tracking-widest">
                  {section.section_number}. {section.section_name}
                </p>
              </div>
              <div className="divide-y divide-surface-border">
                {(section.items ?? []).map((item) => (
                  <div key={item.item_number} className="px-4 py-3 flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-ink-primary leading-snug">{item.description}</p>
                      <p className="text-xs text-ink-muted mt-0.5">{item.unit}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-medium text-ink-primary">{fmt(item.client_unit_cost)}</p>
                      <p className="text-xs text-ink-muted">per {item.unit}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="px-4 py-2.5 border-t border-surface-border flex justify-between items-center bg-surface-overlay">
                <p className="text-xs text-ink-muted">Section subtotal</p>
                <p className="text-sm font-semibold text-ink-primary">{fmt(section.section_client_total)}</p>
              </div>
            </div>
          ))}

          {/* Grand total */}
          <div className="bg-siglat-teal/10 border border-siglat-teal/30 rounded-xl p-4">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm font-semibold text-ink-primary">Estimated Total</p>
                <p className="text-xs text-ink-muted mt-0.5">May vary based on site conditions</p>
              </div>
              <p className="text-xl font-bold text-siglat-teal">
                {fmt(totalMin)} &ndash; {fmt(totalMax)}
              </p>
            </div>
            <p className="text-xs text-ink-muted mt-2">All materials supplied by Siglat Konstract</p>
          </div>

          {/* Flags */}
          {flags.length > 0 && (
            <div className="bg-yellow-500/10 border border-yellow-500/25 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-yellow-400" />
                <p className="text-sm font-medium text-yellow-400">Notes from Siglat Konstract</p>
              </div>
              <ul className="flex flex-col gap-1">
                {flags.map((f, i) => (
                  <li key={i} className="text-xs text-yellow-300">{f}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Feedback form */}
          {!fbSent ? (
            <div className="bg-surface-raised border border-surface-border rounded-xl p-4">
              <p className="text-sm font-semibold text-ink-primary mb-3">
                How well did we understand your problem?
              </p>

              {/* Star rating */}
              <div className="flex gap-1 mb-4">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setFbRating(n)}
                    onMouseEnter={() => setFbHover(n)}
                    onMouseLeave={() => setFbHover(0)}
                    className="p-0.5 transition-transform active:scale-90"
                  >
                    <Star
                      className={`w-7 h-7 transition-colors ${
                        n <= (fbHover || fbRating)
                          ? 'text-yellow-400 fill-yellow-400'
                          : 'text-surface-border'
                      }`}
                    />
                  </button>
                ))}
              </div>

              {/* Accuracy */}
              <div className="flex flex-col gap-2 mb-4">
                {ACCURACY_OPTIONS.map((opt) => (
                  <label key={opt.val} className="flex items-center gap-2.5 cursor-pointer">
                    <input
                      type="radio"
                      name="fb-accuracy"
                      value={opt.val}
                      checked={fbAccuracy === opt.val}
                      onChange={() => setFbAccuracy(opt.val)}
                      className="accent-siglat-teal w-4 h-4"
                    />
                    <span className="text-sm text-ink-primary">{opt.label}</span>
                  </label>
                ))}
              </div>

              {/* Optional notes */}
              <textarea
                placeholder="Tell us what we missed (optional)"
                value={fbNotes}
                onChange={(e) => setFbNotes(e.target.value)}
                rows={2}
                className="w-full bg-surface-overlay border border-surface-border rounded-lg px-3 py-2 text-sm text-ink-primary placeholder:text-ink-muted resize-none focus:outline-none focus:ring-1 focus:ring-siglat-teal mb-3"
              />

              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={submitFeedback}
                  disabled={fbSending || (!fbRating && !fbAccuracy)}
                >
                  {fbSending ? 'Sending\u2026' : 'Submit Feedback'}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setFbSent(true)}>
                  Skip
                </Button>
              </div>
            </div>
          ) : (
            <div className="bg-surface-raised border border-surface-border rounded-xl p-4 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-siglat-teal" />
              <p className="text-sm text-ink-muted">Thank you for your feedback.</p>
            </div>
          )}

          <p className="text-xs text-ink-muted text-center px-4 pb-2">
            This is a System Estimate only. Final pricing confirmed after site inspection.
          </p>

        </div>
      </div>

      {/* Bottom CTAs */}
      <div className="shrink-0 bg-surface-raised border-t border-surface-border px-4 pt-3 pb-4 safe-bottom">
        <div className="page-container flex flex-col gap-2">

          {/* Row: PDF + Think First */}
          <div className="flex gap-2">
            <Button
              fullWidth
              variant="secondary"
              size="md"
              leftIcon={<FileDown className="w-4 h-4" />}
              disabled
              title="PDF download coming soon"
            >
              Download PDF
            </Button>
            <Button
              fullWidth
              variant="secondary"
              size="md"
              onClick={handleThinkFirst}
            >
              I'll Think First
            </Button>
          </div>

          {/* Primary CTA */}
          <Button
            fullWidth
            size="lg"
            onClick={handleFinalProposal}
            rightIcon={<ChevronRight className="w-4 h-4" />}
          >
            Request Final Proposal
          </Button>

          {/* Soft escape */}
          <button
            type="button"
            onClick={handleStartNew}
            className="w-full text-center text-sm text-ink-muted py-1 hover:text-ink-primary transition-colors"
          >
            Start New Service
          </button>

        </div>
      </div>

    </div>
  )
}
